import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Icon,
  useToast,
  Spinner,
  Badge,
  Box,
  Divider,
  Alert,
  AlertIcon,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import { FaCalendarPlus, FaSync, FaTrash, FaCheck } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { SiMicrosoftoutlook } from 'react-icons/si';
import { url } from '../../crudRequests';
import {formatDateSpelled} from "../../utils/appLocale";

export default function CalendarSync({ isOpen, onClose, state, onSync }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const toast = useToast();

  // Check calendar connection status on mount
  useEffect(() => {
    if (isOpen) {
      checkCalendarStatus();
    }
  }, [isOpen]);

  // Check for OAuth callback parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar') === 'connected') {
      toast({
        title: 'Calendar Connected',
        description: 'Your calendar has been successfully connected.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      checkCalendarStatus();
    } else if (params.get('error')) {
      const error = params.get('error');
      toast({
        title: 'Connection Failed',
        description: `Failed to connect calendar: ${error}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const checkCalendarStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${url}/api/calendar/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${state.jwt}`
        },
        body: JSON.stringify({
          db: state.db || 'blh'
        })
      });

      const data = await response.json();
      if (data.success) {
        setConnections(data.connections || []);
      }
    } catch (error) {
      console.error('Error checking calendar status:', error);
      toast({
        title: 'Error',
        description: 'Failed to check calendar connection status',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const connectCalendar = async (provider) => {
    try {
      setConnectingProvider(provider);
      const response = await fetch(`${url}/api/calendar/connect/${provider}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${state.jwt}`
        },
        body: JSON.stringify({
          jwt: state.jwt,
          db: state.db || 'blh'
        })
      });

      const data = await response.json();
      console.log('Calendar connect response:', data);

      if (data.success) {
        if (data.authUrl) {
          // Production: Open OAuth in popup window
          console.log('Opening OAuth popup:', data.authUrl);
          const width = 500;
          const height = 600;
          const left = window.screen.width / 2 - width / 2;
          const top = window.screen.height / 2 - height / 2;

          const popup = window.open(
            data.authUrl,
            'google-oauth',
            `width=${width},height=${height},left=${left},top=${top}`
          );

          // Simple approach: just poll for changes after popup closes
          // Check every 2 seconds if popup is closed and refresh status
          const checkInterval = setInterval(() => {
            try {
              // Try to check if popup is closed
              // This might throw COOP error but we catch it
              if (!popup || popup.closed) {
                clearInterval(checkInterval);
                // Wait a bit then refresh status
                setTimeout(() => {
                  checkCalendarStatus();
                }, 1000);
              }
            } catch (e) {
              // If we can't check popup status due to COOP,
              // just clear interval and check status
              console.log('Cannot check popup status, checking calendar status instead');
              clearInterval(checkInterval);
              setTimeout(() => {
                checkCalendarStatus();
              }, 1000);
            }
          }, 2000);

          // Stop checking after 2 minutes
          setTimeout(() => {
            clearInterval(checkInterval);
          }, 120000);
        } else {
          // Development: simulated connection
          toast({
            title: 'Calendar Connected',
            description: data.message || `${provider} calendar connected successfully`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          checkCalendarStatus();
        }
      }
    } catch (error) {
      console.error(`Error connecting ${provider}:`, error);
      toast({
        title: 'Connection Failed',
        description: `Failed to connect ${provider} calendar`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setConnectingProvider(null);
    }
  };

  const disconnectCalendar = async (provider) => {
    try {
      // If provider is undefined or 'all', use force disconnect
      const endpoint = !provider || provider === 'all'
        ? `${url}/api/calendar/force-disconnect-all`
        : `${url}/api/calendar/disconnect`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${state.jwt}`
        },
        body: JSON.stringify({
          provider,
          db: state.db || 'blh'
        })
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Calendar Disconnected',
          description: provider && provider !== 'all'
            ? `${provider} calendar has been disconnected`
            : 'All calendar connections have been removed',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        checkCalendarStatus();
      }
    } catch (error) {
      console.error(`Error disconnecting ${provider}:`, error);
      toast({
        title: 'Error',
        description: `Failed to disconnect ${provider} calendar`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };


  const syncCalendars = async () => {
    try {
      setSyncing(true);
      const response = await fetch(`${url}/api/calendar/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${state.jwt}`
        },
        body: JSON.stringify({
          db: state.db || 'blh'
        })
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Calendar Synced',
          description: data.message || 'Your appointments have been synced to your calendar',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Notify parent component
        if (onSync) onSync();

        checkCalendarStatus();
      } else {
        toast({
          title: 'Sync Failed',
          description: data.message || 'Failed to sync appointments',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error syncing calendars:', error);
      toast({
        title: 'Sync Error',
        description: 'Failed to sync appointments to calendar',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSyncing(false);
    }
  };

  const isGoogleConnected = connections.some(c => c.provider === 'google');
  const isMicrosoftConnected = connections.some(c => c.provider === 'microsoft');
  const hasConnections = connections.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Calendar Sync Settings</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {loading ? (
            <Box textAlign="center" py={8}>
              <Spinner size="lg" color="blue.500" />
              <Text mt={4}>Loading calendar connections...</Text>
            </Box>
          ) : (
            <VStack spacing={6} align="stretch">
              {/* Connection Status */}
              {hasConnections && (
                <>
                  <Alert status="success" rounded="md">
                    <AlertIcon />
                    <Text fontSize="sm">
                      Your calendar{connections.length > 1 ? 's are' : ' is'} connected and ready to sync
                    </Text>
                  </Alert>

                  <VStack spacing={3} align="stretch">
                    {connections.map(conn => (
                      <HStack
                        key={conn.provider}
                        p={3}
                        borderWidth="1px"
                        borderColor="gray.200"
                        rounded="md"
                        justify="space-between"
                      >
                        <HStack>
                          <Icon
                            as={conn.provider === 'google' ? FcGoogle : SiMicrosoftoutlook}
                            boxSize={6}
                          />
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium" textTransform="capitalize">
                              {conn.provider} Calendar
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {conn.email}
                            </Text>
                          </VStack>
                        </HStack>
                        <HStack>
                          {conn.lastSync && (
                            <Text fontSize="xs" color="gray.500">
                              Last synced: {formatDateSpelled(conn.lastSync)}
                            </Text>
                          )}
                          <Tooltip label={`Disconnect ${conn.provider}`}>
                            <IconButton
                              icon={<FaTrash />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => disconnectCalendar(conn.provider)}
                            />
                          </Tooltip>
                        </HStack>
                      </HStack>
                    ))}
                  </VStack>
                </>
              )}

              {/* Connect New Calendars */}
              <Box>
                <Text fontWeight="semibold" mb={3}>
                  {hasConnections ? 'Connect Additional Calendars' : 'Connect Your Calendar'}
                </Text>

                <VStack spacing={3}>
                  {!isGoogleConnected && (
                    <Button
                      leftIcon={<FcGoogle />}
                      variant="outline"
                      isFullWidth
                      onClick={() => connectCalendar('google')}
                      isLoading={connectingProvider === 'google'}
                      loadingText="Connecting..."
                    >
                      Connect Google Calendar
                    </Button>
                  )}

                  {!isMicrosoftConnected && (
                    <Button
                      leftIcon={<SiMicrosoftoutlook color="#0078d4" />}
                      variant="outline"
                      isFullWidth
                      onClick={() => connectCalendar('microsoft')}
                      isLoading={connectingProvider === 'microsoft'}
                      loadingText="Connecting..."
                    >
                      Connect Outlook Calendar
                    </Button>
                  )}
                </VStack>
              </Box>

              {/* Info */}
              <Box bg="gray.50" p={3} rounded="md">
                <Text fontSize="sm" color="gray.600">
                  <Text as="span" fontWeight="semibold">How it works:</Text>
                  <br />
                  • Connect your calendar once
                  <br />
                  • All appointments automatically sync
                  <br />
                  • Updates sync in real-time
                  <br />
                  • Your data remains private and secure
                </Text>
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            {hasConnections && (
              <Button
                bg="black"
                color="white"
                _hover={{ bg: "gray.800" }}
                leftIcon={<FaSync />}
                onClick={syncCalendars}
                isLoading={syncing}
                loadingText="Syncing..."
              >
                Sync Now
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}